import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  ComplianceSnapshotFragment,
  SaleInvoiceDetailFragment,
  SaleInvoiceUpsertSidepanelQuery,
} from 'generated/resources/graphql'

import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { countryComplianceExtraFieldsConfig } from 'modules/finance/components/Invoices/SaleInvoicesUpsertSidepanel/lib/complianceExtraFields'
import { SaleInvoiceDocument, SalesInvoiceProject } from 'modules/finance/lib/salesInvoiceUtils'

import ComplianceSnapshots from './_ComplianceSnapshots'
import LineItems from './_LineItems'
import RelatedFinancialDocuments from './_RelatedFinancialDocuments'
import Summary from './_Summary'

type FinanceFinancialDocumentComplianceExtraFieldsConfig = NonNullable<
  SaleInvoiceUpsertSidepanelQuery['finance']['financialDocumentComplianceExtraFieldsConfigs']
>[number]

interface MainContentProps {
  locale: string
  saleInvoice: SaleInvoiceDetailFragment
  complianceExtraFieldsConfigs?: FinanceFinancialDocumentComplianceExtraFieldsConfig[]
  relatedFinancialDocumentPath: (relatedInvoiceId: string) => string
  complianceSnapshots?: ComplianceSnapshotFragment[]
  relatedProject?: SalesInvoiceProject
}

const MainContent = ({
  locale,
  saleInvoice,
  complianceExtraFieldsConfigs = [],
  relatedFinancialDocumentPath,
  complianceSnapshots = [],
  relatedProject,
}: MainContentProps) => {
  const saleInvoiceDocument: SaleInvoiceDocument = {
    ...saleInvoice,
    issuerCountryCode: saleInvoice.issuerCountryCode || saleInvoice.legalEntity?.country,
  }

  const invoiceComplianceExtraFieldsConfig = useUnsafeMemo(() => {
    return countryComplianceExtraFieldsConfig(
      complianceExtraFieldsConfigs,
      saleInvoiceDocument.issuerCountryCode || undefined
    )
  }, [complianceExtraFieldsConfigs, saleInvoiceDocument.issuerCountryCode])

  return (
    <Box gap="s24" flexDirection="column" paddingBottom="s48">
      <Box paddingLeft="s6">
        <Text.Normal weight="semibold" color="grey1000">
          {i18n.t('finance.sale_invoices.detail.summary')}
        </Text.Normal>
      </Box>
      <Box gap="s16" width="full">
        <Summary
          saleInvoice={saleInvoiceDocument}
          complianceExtraFieldsConfigsByCountry={invoiceComplianceExtraFieldsConfig}
          relatedProject={relatedProject}
        />
        <LineItems
          locale={locale}
          saleInvoice={saleInvoiceDocument}
          countryComplianceExtraFieldsConfig={invoiceComplianceExtraFieldsConfig}
        />
      </Box>

      {saleInvoice.financialDocumentEvents.length > 0 && (
        <>
          <Box paddingLeft="s6">
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('finance.sale_invoices.detail.related_financial_documents.title')}
            </Text.Normal>
          </Box>
          <Box gap="s16" width="full">
            <RelatedFinancialDocuments
              locale={locale}
              saleInvoice={saleInvoice}
              relatedFinancialDocumentPath={relatedFinancialDocumentPath}
            />
          </Box>
        </>
      )}

      {complianceSnapshots.length > 0 && (
        <>
          <Box paddingLeft="s6">
            <Text.Normal weight="semibold" color="grey1000">
              {i18n.t('finance.sale_invoices.detail.compliance_snapshots.title')}
            </Text.Normal>
          </Box>
          <Box gap="s16" width="full">
            <ComplianceSnapshots
              complianceSnapshots={complianceSnapshots}
              complianceExtraFieldsConfigs={complianceExtraFieldsConfigs}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default MainContent
